import { FC, FocusEvent, useEffect, useRef, useState } from "react";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { Heading, Text } from "@otrium/atoms";
import { ClockIcon, LockIcon } from "@otrium/icons";
import { theme } from "src/theme";
import { Button } from "src/atoms/Button";
import Input from "src/atoms/Input";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { ChevronLeft } from "src/icons/ChevronLeft";
import { CheckCircleIcon } from "src/icons/CheckCircleIcon";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { locale } from "src/config/app-config";
import { getStorageJSON, setStorageJSON } from "src/utils/helper/localStorage";
import { OT_GUEST_LOOKUP_ATTEMPTS } from "src/utils/constants";
import type { GuestLookupIntent } from "src/stores/header/header.store";
import {
  getOrderNumberPlaceholder,
  normalizeOrderNumber,
  ORDER_NUMBER_PATTERN,
} from "src/utils/orderNumber";
import { trackReturnLinkRequestedEvent } from "src/tracking/events/return-link-requested";
import { trackOrderTrackingRequestedEvent } from "src/tracking/events/order-tracking-requested";
import {
  LookupPanel,
  PanelHeader,
  BackButton,
  SecureLine,
  FieldGroup,
  FieldLabel,
  HelperText,
  SecurityNote,
  StatusPanel,
  UnderlineLink,
} from "./GuestOrderLookup.styled";

const SOFT_LIMIT = 3;
const SOFT_WINDOW_MS = 10 * 60 * 1000;
const SOFT_COOLDOWN_MS = 5 * 60 * 1000;
const HARD_LIMIT = 8;
const HARD_COOLDOWN_MS = 2 * 60 * 60 * 1000;

type Throttle = { kind: "soft" | "hard"; until: number } | null;

function readAttempts(now: number): number[] {
  const stored = getStorageJSON<number[]>(OT_GUEST_LOOKUP_ATTEMPTS) || [];

  return stored.filter((ts) => now - ts < HARD_COOLDOWN_MS);
}

function evaluateThrottle(attempts: number[], now: number): Throttle {
  const last = attempts[attempts.length - 1] || 0;

  if (attempts.length > HARD_LIMIT && last + HARD_COOLDOWN_MS > now) {
    return { kind: "hard", until: last + HARD_COOLDOWN_MS };
  }

  const recent = attempts.filter((ts) => now - ts < SOFT_WINDOW_MS);
  if (recent.length > SOFT_LIMIT && last + SOFT_COOLDOWN_MS > now) {
    return { kind: "soft", until: last + SOFT_COOLDOWN_MS };
  }

  return null;
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

interface FormValues {
  orderNumber: string;
  email: string;
}

interface Props {
  intent: GuestLookupIntent;
  onBack: () => void;
}

const GuestOrderLookup: FC<Props> = ({ intent, onBack }) => {
  const { _, i18n } = useLingui();
  const [isSuccess, setIsSuccess] = useState(false);
  const [throttle, setThrottle] = useState<Throttle>(() => {
    const now = Date.now();

    return evaluateThrottle(readAttempts(now), now);
  });
  const [countdown, setCountdown] = useState("");
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!throttle) {
      return;
    }

    const update = () => {
      const now = Date.now();
      if (now >= throttle.until) {
        setThrottle(evaluateThrottle(readAttempts(now), now));

        return;
      }
      setCountdown(formatCountdown(throttle.until - now));
    };

    update();
    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [throttle]);

  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    initialValues: {
      orderNumber: "",
      email: "",
    },
    validationSchema: Yup.object().shape({
      orderNumber: Yup.string()
        .required(FORM_ERROR_MESSAGES.orderNumberRequired)
        // matches() reads an object second arg as options, so the descriptor
        // must ride in as { message } or prod builds fall back to yup's default.
        .matches(ORDER_NUMBER_PATTERN, {
          message: FORM_ERROR_MESSAGES.orderNumber,
        }),
      email: Yup.string()
        .required(FORM_ERROR_MESSAGES.emailRequired)
        .email(FORM_ERROR_MESSAGES.email),
    }),
    onSubmit: async (values) => {
      const now = Date.now();
      setStorageJSON(OT_GUEST_LOOKUP_ATTEMPTS, [...readAttempts(now), now]);

      const payload = {
        orderNumber: values.orderNumber,
        email: values.email.trim(),
      };
      if (intent === "return") {
        void trackReturnLinkRequestedEvent(payload);
      } else {
        void trackOrderTrackingRequestedEvent(payload);
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsSuccess(true);
    },
  });

  const handleOrderNumberBlur = (e: FocusEvent<HTMLInputElement>) => {
    const normalized = normalizeOrderNumber(e.target.value);
    if (normalized !== e.target.value) {
      void formik.setFieldValue("orderNumber", normalized);
    }
    formik.handleBlur(e);
  };

  const handleTryDifferentEmail = () => {
    void formik.setFieldValue("email", "");
    void formik.setFieldTouched("email", false);
    setIsSuccess(false);
    const now = Date.now();
    setThrottle(evaluateThrottle(readAttempts(now), now));
  };

  const title =
    intent === "return" ? (
      <Trans>Email me my return link</Trans>
    ) : (
      <Trans>Email me my tracking link</Trans>
    );

  const customerServiceLink = (
    <UnderlineLink
      href={getI18nRoute({ route: "/faq", locale })}
      target="_blank"
    >
      <Trans>Contact customer service</Trans>
    </UnderlineLink>
  );

  if (isSuccess) {
    const orderNumber = formik.values.orderNumber;
    const email = formik.values.email.trim();

    return (
      <LookupPanel role="status">
        <StatusPanel>
          <CheckCircleIcon
            width={36}
            height={36}
            color={theme.colors.primary.forestGreen}
          />
          <Heading
            as="h2"
            fontFamily="PlusJakartaSans"
            fontSize={16}
            fontWeight={700}
            lineHeight="24px"
            color="tone.black"
          >
            <Trans>Check your email</Trans>
          </Heading>
          <Text fontSize={14} lineHeight="20px" color="tone.anthraciteDark">
            {intent === "return" ? (
              <Trans>
                If {orderNumber} was ordered with {email}, your return link is
                on its way. Allow up to 5 minutes.
              </Trans>
            ) : (
              <Trans>
                If {orderNumber} was ordered with {email}, your tracking link is
                on its way. Allow up to 5 minutes.
              </Trans>
            )}
          </Text>
          <Button variant="primary" fullWidth onClick={handleTryDifferentEmail}>
            <Trans>Try a different email address</Trans>
          </Button>
          {customerServiceLink}
        </StatusPanel>
      </LookupPanel>
    );
  }

  if (throttle?.kind === "hard") {
    return (
      <LookupPanel role="status">
        <StatusPanel>
          <ClockIcon
            width={32}
            height={32}
            color={theme.colors.tone.anthraciteDark}
          />
          <Heading
            as="h2"
            fontFamily="PlusJakartaSans"
            fontSize={16}
            fontWeight={700}
            lineHeight="24px"
            color="tone.black"
          >
            <Trans>Paused for about 2 hours</Trans>
          </Heading>
          <Text fontSize={14} lineHeight="20px" color="tone.anthraciteDark">
            <Trans>
              Order lookups from this device are paused to keep them secure.
              Nothing&apos;s wrong with your order. Customer service can send
              your link straight away.
            </Trans>
          </Text>
          {customerServiceLink}
        </StatusPanel>
      </LookupPanel>
    );
  }

  const isThrottled = throttle?.kind === "soft";
  const time = countdown;

  return (
    <LookupPanel>
      <PanelHeader>
        <BackButton
          type="button"
          aria-label={t(i18n)`Back`}
          data-testid="guest-lookup-back"
          onClick={onBack}
        >
          <ChevronLeft stroke={theme.colors.tone.black} strokeWidth="2" />
        </BackButton>
        {isThrottled && (
          <ClockIcon
            width={24}
            height={24}
            color={theme.colors.tone.anthraciteDark}
          />
        )}
        <Heading
          as="h2"
          fontFamily="PlusJakartaSans"
          fontSize={16}
          fontWeight={700}
          lineHeight="24px"
          color="tone.black"
          tabIndex={-1}
          ref={titleRef}
        >
          {isThrottled ? <Trans>Paused for a moment</Trans> : title}
        </Heading>
      </PanelHeader>

      <SecureLine role={isThrottled ? "status" : undefined}>
        {isThrottled ? (
          <Trans>
            To keep order lookups secure, we pause them after a few tries in a
            row. Nothing&apos;s wrong with your order. The timer shows when you
            can try again.
          </Trans>
        ) : (
          <>
            <LockIcon
              width={12}
              height={12}
              color={theme.colors.tone.anthracite}
            />
            <Trans>Secure · no sign-in required</Trans>
          </>
        )}
      </SecureLine>

      <form noValidate onSubmit={formik.handleSubmit}>
        <FieldGroup>
          <FieldLabel htmlFor="guest-lookup-order-number">
            <Trans>Order number</Trans>
          </FieldLabel>
          <Input
            {...formik.getFieldProps("orderNumber")}
            id="guest-lookup-order-number"
            inputTestId="guest-lookup-order-number"
            type="text"
            placeholder={getOrderNumberPlaceholder()}
            onBlur={handleOrderNumberBlur}
            disabled={isThrottled}
            errorMessage={
              formik.touched.orderNumber && formik.errors.orderNumber
                ? _(formik.errors.orderNumber)
                : undefined
            }
            fullWidth
          />
          <HelperText>
            <Trans>
              Top of your order confirmation email, and on the packing slip in
              your parcel.
            </Trans>
          </HelperText>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="guest-lookup-email">
            <Trans>Email address</Trans>
          </FieldLabel>
          <Input
            {...formik.getFieldProps("email")}
            id="guest-lookup-email"
            inputTestId="guest-lookup-email"
            type="email"
            placeholder="name@example.com"
            disabled={isThrottled}
            errorMessage={
              formik.touched.email && formik.errors.email
                ? _(formik.errors.email)
                : undefined
            }
            fullWidth
          />
          <HelperText>
            <Trans>
              The address you ordered with. A guest order may sit under a
              different one.
            </Trans>
          </HelperText>
        </FieldGroup>

        <Button
          variant="primary"
          type="submit"
          fullWidth
          disabled={
            isThrottled ||
            formik.isSubmitting ||
            !formik.values.orderNumber ||
            !formik.values.email
          }
          loading={formik.isSubmitting}
        >
          {isThrottled
            ? t(i18n)`Try again in ${time}`
            : t(i18n)`Email me the link`}
        </Button>
        <SecurityNote>
          <Trans>
            For your security we email the link rather than showing order
            details here.
          </Trans>
        </SecurityNote>
      </form>
    </LookupPanel>
  );
};

export { GuestOrderLookup };
